import React, {Fragment, useContext, useEffect, useRef, useState} from 'react';
import {Col, Dropdown, Form, Spinner, Button, Card, ButtonGroup} from "react-bootstrap";
import { Context } from "../../index";
import { observer } from "mobx-react-lite";
import {
    createBook,
    fetchCurrentUserBooks,
} from "../../http/bookAPI";
import Row from "react-bootstrap/Row";
import AvatarEditor from 'react-avatar-editor';
import defaultImage from "../../assets/empty-cover.jpg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {getRedirectUrlApi} from "../../http/userAPI";
import { findReviewerPenNameConflict, ReviewerNameSafetyModal } from "../../helpers/ReviewerNameSafety";


const addBookInDashboard = observer(() => {
    const { book } = useContext(Context);
    const { user } = useContext(Context);

    const [titleState, setTitleState] = useState('');
    const [subtitleState, setSubTitleState] = useState('');
    const [authorState, setAuthorState] = useState('');
    const [summaryState, setSummaryState] = useState('');
    const [coverFileState, setCoverFileState] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [coverPreview, setCoverPreview] = useState(defaultImage);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reviewerSafetyConflict, setReviewerSafetyConflict] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [scale, setScale] = useState(1);
    const [editorRef, setEditorRef] = useState(null);
    const [editorSize, setEditorSize] = useState({ width: 240, height: 360 });
    const [bookFormat, setBookFormat] = useState("6x9");

    const [noManuscript, setNoManuscript] = useState(false);
    const [publicCatalogVisible, setPublicCatalogVisible] = useState(true);

    const [paperbackAsinState, setPaperBackAsinState] = useState('');
    const [kindleAsinState, setKindleAsinState] = useState('');
    const [hardcoverAsinState, setHardCoverAsinState] = useState('');
    const [audioAsinState, setAudioAsinState] = useState('');
    const [audibleAsinState, setAudibleAsinState] = useState('');
    const [audioBookLinkState, setAudioBookLinkState] = useState('');
    const [selectedWordCount, setSelectedWordCount ] = useState({
        id: 1, count: 10000, label: "About 10,000"
    });

    const [territoriesMode, setTerritoriesMode] = useState("WORLDWIDE"); // WORLDWIDE | MARKETPLACES
    const [availableMarketplaces, setAvailableMarketplaces] = useState([]);
    const [showMarketplaces, setShowMarketplaces] = useState(false);


    const MARKETPLACE_OPTIONS = [
        { code: 'com', label: 'Amazon.com (US)' },
        { code: 'co.uk', label: 'Amazon.co.uk (UK)' },
        { code: 'ca', label: 'Amazon.ca (CA)' },

        { code: 'de', label: 'Amazon.de (DE)' },
        { code: 'fr', label: 'Amazon.fr (FR)' },
        { code: 'it', label: 'Amazon.it (IT)' },
        { code: 'es', label: 'Amazon.es (ES)' },

        { code: 'co.jp', label: 'Amazon.co.jp (JP)' },
        { code: 'com.au', label: 'Amazon.com.au (AU)' },
        { code: 'in', label: 'Amazon.in (IN)' },

        { code: 'com.mx', label: 'Amazon.com.mx (MX)' },
        { code: 'com.br', label: 'Amazon.com.br (BR)' },
    ];


    const hasStoredValue = (value) =>
        value !== undefined && value !== null && String(value).trim() !== "";

    const storedBool = (value, fallback = false) => {
        if (value === undefined || value === null || value === "") return fallback;
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value === 1;

        const normalized = String(value).trim().toLowerCase();
        if (["true", "1", "yes", "on"].includes(normalized)) return true;
        if (["false", "0", "no", "off"].includes(normalized)) return false;
        return fallback;
    };

    const getStoredBookTypes = (bookData = {}) => {
        const storedTypes = bookData.bookTypes || {};

        return {
            paper_back_flag: storedBool(
                storedTypes.paperBack ?? storedTypes.paper_back_flag ?? bookData.paper_back_flag ?? bookData.pb ?? bookData.paperBack,
                hasStoredValue(bookData.pb_asin || bookData.paper_back_asin)
            ),
            kindle_flag: storedBool(
                storedTypes.kindle ?? storedTypes.kindle_flag ?? bookData.kindle_flag ?? bookData.kindle,
                hasStoredValue(bookData.k_asin || bookData.kindle_asin)
            ),
            hard_cover_flag: storedBool(
                storedTypes.hardCover ?? storedTypes.hard_cover_flag ?? bookData.hard_cover_flag ?? bookData.hc ?? bookData.hardCover,
                hasStoredValue(bookData.hc_asin || bookData.hard_cover_asin)
            ),
            kindle_unlimited_flag: storedBool(
                storedTypes.kindle_u ?? storedTypes.kindleUnlimited ?? storedTypes.kindle_unlimited_flag ?? bookData.kindle_unlimited_flag ?? bookData.kindle_unlimited,
                false
            ),
            audio_flag: storedBool(
                storedTypes.audio ?? storedTypes.audio_flag ?? bookData.audio_flag ?? bookData.audio,
                hasStoredValue(bookData.audio_asin)
            ),
            audio_exclusive_flag: storedBool(
                storedTypes.audio_ex ?? storedTypes.audio_exclusive_flag ?? bookData.audio_exclusive_flag,
                false
            ),
            audio_non_exclusive_flag: storedBool(
                storedTypes.audio_non_ex ?? storedTypes.audio_non_exclusive_flag ?? bookData.audio_non_exclusive_flag,
                false
            ),
        };
    };

    const normalizeLookup = (value) =>
        String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

    const matchStoredLanguage = (storedLanguage) => {
        if (storedLanguage?.id) return storedLanguage;
        const needle = normalizeLookup(storedLanguage?.language || storedLanguage?.language_short || storedLanguage);
        if (!needle || !Array.isArray(book.languages)) return null;
        return book.languages.find((item) =>
            normalizeLookup(item.language) === needle ||
            normalizeLookup(item.language_short) === needle ||
            String(item.id) === String(storedLanguage?.id || "")
        ) || null;
    };

    const matchStoredGenre = (storedGenre) => {
        if (storedGenre?.id) return storedGenre;
        const needle = normalizeLookup(storedGenre?.genre || storedGenre?.category || storedGenre);
        if (!needle || !Array.isArray(book.genres)) return null;
        return book.genres.find((item) =>
            normalizeLookup(item.genre) === needle ||
            normalizeLookup(item.category) === needle ||
            String(item.id) === String(storedGenre?.id || "")
        ) || null;
    };

    const getFirstAvailableAmazonAsin = () => (
        kindleAsinState ||
        paperbackAsinState ||
        hardcoverAsinState ||
        audioAsinState ||
        audibleAsinState ||
        ''
    ).trim().toUpperCase();

    const openMarketplaceAmazonLink = async (marketplaceCode, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const asin = getFirstAvailableAmazonAsin();
        if (!/^[A-Z0-9]{10}$/.test(asin)) {
            toast.info("Add at least one ASIN before opening Amazon.", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
            return;
        }

        try {
            const response = await getRedirectUrlApi(`https://www.amazon.${marketplaceCode}/dp/${asin}`);
            if (response?.redirectUrl) {
                window.open(response.redirectUrl, "_blank", "noopener noreferrer");
            }
        } catch (error) {
            toast.error("Failed to open Amazon marketplace.", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
        }
    };

    const renderMarketplaceLink = (m, text = m.label) => (
        <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
            onClick={(event) => openMarketplaceAmazonLink(m.code, event)}
        >
            {text}
        </button>
    );

    const parentRef = useRef(null);

    const [newBook, setNewdBook] = useState({
        message_flag: false,
        paper_back_flag: false,
        kindle_flag: true,
        kindle_unlimited_flag: false,
        hard_cover_flag: false,
        audio_flag: false,
        audio_exclusive_flag: false,
        audio_non_exclusive_flag: false,
        cover_file: null,
        summary: null,
        book_file: null,
        bp_asin: null,
        k_asin: null,
        hc_asin: null,
        audio_asin: null,
        book_temp_file: null,

    });

    const [step, setStep] = useState(0);
    const [nextStep, setNextStep] = useState({
        step0: true,
        step1: false,
        step2: false,
        step3: false,
        step4: false,
        step5: false,
        step6: false,
    });

    const wordCountOptions = [
        { id: 1, count: 1000, label: "About 1,000" },
        { id: 2, count: 5000, label: "About 5,000" },
        { id: 3, count: 10000, label: "About 10,000" },
        { id: 4, count: 15000, label: "About 15,000" },
        { id: 5, count: 20000, label: "About 20,000" },
        { id: 6, count: 25000, label: "About 25,000" },
        { id: 7, count: 30000, label: "About 30,000" },
        { id: 8, count: 35000, label: "About 35,000" },
        { id: 9, count: 40000, label: "About 40,000" },
        { id: 10, count: 45000, label: "About 45,000" },
        { id: 11, count: 50000, label: "About 50,000" },
        { id: 12, count: 60000, label: "About 60,000" },
        { id: 13, count: 75000, label: "About 75,000" },
        { id: 14, count: 85000, label: "About 85,000" },
        { id: 15, count: 100000, label: "About 100,000" },
        { id: 16, count: 120000, label: "More then 120,000" },

    ];



    const [, setPdfFile] = useState(null)
    const [tempPDFFile, setTempPDFFile] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [pdfFileTitle, setPdfFileTitle] = useState("")

    useEffect(() => {
        const bookSizes = {
            "6x9": { aspectRatio: 6 / 9 },
            "8.5x11": { aspectRatio: 8.5 / 11 },
            "8.5x8.5": { aspectRatio: 8.5 / 8.5 },
            "8x10": { aspectRatio: 8 / 10 },
            "5x8": { aspectRatio: 5 / 8 },
            "7x10": { aspectRatio: 7 / 10 },
        };

        const updateSize = () => {
            if (parentRef.current) {
                const parentWidth = parentRef.current.clientWidth;
                const selectedSize = bookSizes[bookFormat] || bookSizes["6x9"];
                const newHeight = parentWidth / selectedSize.aspectRatio;
                setEditorSize({ width: parentWidth, height: newHeight });
            }
        };

        updateSize();
        window.addEventListener("resize", updateSize);
        return () => window.removeEventListener("resize", updateSize);
    }, [bookFormat]);

    useEffect(() => {
        loadBookDataFromLocalStorage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;

        setNewdBook((prevBook) => {
            let updatedBook = { ...prevBook, [name]: checked };

            if (name === "audio_exclusive_flag" && checked) {
                updatedBook.audio_non_exclusive_flag = false;
            }

            if (name === "audio_non_exclusive_flag" && checked) {
                updatedBook.audio_exclusive_flag = false;
            }

            if (name === "audio_flag" && !checked) {
                updatedBook.audio_exclusive_flag = false;
                updatedBook.audio_non_exclusive_flag = false;
            }

            if (name === "kindle_flag" && !checked) {
                updatedBook.kindle_unlimited_flag = false;
            }

            return updatedBook;
        });
    };


    const manuscriptRequirementSatisfied = noManuscript || Boolean(tempPDFFile && pdfFileTitle);
    const manuscriptUploadInProgress = (uploadProgress > 0 && uploadProgress < 100) && !noManuscript;
    const hasAnyBookFormat = Boolean(
        newBook.paper_back_flag ||
        newBook.kindle_flag ||
        newBook.hard_cover_flag ||
        newBook.audio_flag ||
        newBook.kindle_unlimited_flag
    );
    const hasAnyAsin = Boolean(
        paperbackAsinState ||
        kindleAsinState ||
        hardcoverAsinState ||
        audioAsinState ||
        audibleAsinState
    );
    const isFormValid = Boolean(
        user.user.id &&
        book.selectedGenre.id &&
        book.selectedLanguage.id &&
        titleState &&
        authorState &&
        summaryState &&
        hasAnyBookFormat &&
        hasAnyAsin &&
        coverFileState &&
        manuscriptRequirementSatisfied &&
        (!newBook.audio_non_exclusive_flag || audioBookLinkState)
    );

    const getFormValidationMessage = () => {
        if (!user.user.id) return "User session is missing. Please refresh the page.";
        if (!titleState?.trim()) return "Enter the book title, please.";
        if (!authorState?.trim()) return "Enter the author name, please.";
        if (!book.selectedLanguage?.id) return "Select the book language, please.";
        if (!book.selectedGenre?.id) return "Select the book genre, please.";
        if (!hasAnyBookFormat) return "Select at least one book format, please.";
        if (!hasAnyAsin) return "Enter at least one ASIN, please.";
        if (!summaryState?.trim()) return "Enter the book summary, please.";
        if (!coverFileState) return "Upload the book cover image, please.";
        if (!manuscriptRequirementSatisfied) return "Upload a manuscript or tick \"I don't want to upload the manuscript\".";
        if (newBook.audio_non_exclusive_flag && !audioBookLinkState?.trim()) return "Enter the audiobook download link, please.";
        return "Fill all required fields, please.";
    };



    const handleSubmitBookToModeration = (options = {}) => {
        const skipSafety = options?.skipSafety === true;
        const trimmedTitle = titleState.trim();
        const trimmedAuthor = authorState.trim();
        const trimmedSubtitle = subtitleState.trim();

        setTitleState(trimmedTitle);
        setAuthorState(trimmedAuthor);
        setSubTitleState(trimmedSubtitle);

        if (!manuscriptRequirementSatisfied) {
            toast.warn("Upload a manuscript or tick \"I don't want to upload the manuscript\".", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if (!isFormValid) {
            toast.warn(getFormValidationMessage(), { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if (!skipSafety) {
            const conflict = findReviewerPenNameConflict(user?.user?.amz_name, [trimmedAuthor]);
            if (conflict) {
                setReviewerSafetyConflict(conflict);
                return;
            }
        }

        addBook();

    };



    const handleStepsChange = (step) => {
        let error = "";

        if (step === 1 && (!titleState || !authorState)) {
            toast.info(`Enter Title and Author!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;

        } else if (step === 2 &&
            (!book.selectedLanguage.language || !book.selectedGenre.genre ||
                !(newBook.paper_back_flag || newBook.kindle_flag || newBook.hard_cover_flag || newBook.audio_flag))
        ) {
            toast.info(`Set Language, Genre and Type!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        else if (step === 2 && newBook.audio_flag && !(newBook.audio_exclusive_flag || newBook.audio_non_exclusive_flag)) {
            toast.info(`Set Contract Type for Audio Book!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        } else if (step === 4 && !coverFileState) {
            toast.info(`Please Upload Cover of Your Book!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        } else if (step === 5 && !summaryState) {
            toast.info(`Enter the Summary of Book!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        } else if (step === 3 && newBook.kindle_flag === true && !kindleAsinState) {
            toast.info(`Please enter a Kindle ASIN!`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        } else if (step === 3 && newBook.paper_back_flag === true && !paperbackAsinState) {
                toast.info(`Please enter a Paperback ASIN!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
        } else if (step === 3 && newBook.hard_cover_flag === true && !hardcoverAsinState) {
                toast.info(`Please enter a Hard Cover ASIN!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
        } else if (step === 3 && newBook.audio_flag === true && !audioAsinState) {
                toast.info(`Please enter a Amazon Audio ASIN!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
        } else if (step === 3 && (newBook.audio_exclusive_flag === true || newBook.audio_non_exclusive_flag === true) && !audibleAsinState) {
                toast.info(`Please enter a Audible ASIN!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
        } else if (step === 3 && newBook.audio_non_exclusive_flag && !audioBookLinkState) {
            toast.info(`Please, enter a link to download!`,{ position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        } else if (
            step === 2 &&
            territoriesMode === 'MARKETPLACES' &&
            availableMarketplaces.length === 0
        ) {
            toast.info(
                'Pick at least one Marketplace!',
                { position: "bottom-center", autoClose: 3000, hideProgressBar: false }
            );
            return;
        }

        if (error) {
            toast.info(error, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        saveBookDataToLocalStorage({
            title: titleState,
            subtitle: subtitleState,
            author: authorState,
            language: book.selectedLanguage,
            genre: book.selectedGenre,
            audio_book_link: audioBookLinkState,

            summary: summaryState,
            pb_asin: paperbackAsinState,
            k_asin: kindleAsinState,
            hc_asin: hardcoverAsinState,
            audio_asin: audioAsinState,
            audible_asin: audibleAsinState,
            audible_link: audioBookLinkState,


            bookTypes: {
                paperBack: newBook.paper_back_flag,
                kindle: newBook.kindle_flag,
                kindle_u: newBook.kindle_unlimited_flag,
                hardCover: newBook.hard_cover_flag,
                audio: newBook.audio_flag,
                audio_ex: newBook.audio_exclusive_flag,
                audio_non_ex: newBook.audio_non_exclusive_flag,
            }
        });

        setNextStep((prev) => {
            const updatedSteps = Object.keys(prev).reduce((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});

            return {
                ...updatedSteps,
                [`step${step}`]: true, // Включаем только текущий шаг
            };
        });

        setStep(step);
    };


    const saveBookDataToLocalStorage = (updatedFields = {}) => {
        const existingData = JSON.parse(localStorage.getItem("bookForm")) || {};

        const bookData = {
            ...existingData,
            ...updatedFields, // Обновляем только измененные поля
        };
        if (updatedFields.no_manuscript !== undefined) {
            bookData.no_manuscript = updatedFields.no_manuscript ? true : false;
        }
        if (updatedFields.public_catalog_visible !== undefined) {
            bookData.public_catalog_visible = updatedFields.public_catalog_visible ? true : false;
        }
        localStorage.setItem("bookForm", JSON.stringify(bookData));
    };


    const loadBookDataFromLocalStorage = () => {
        const savedData = localStorage.getItem("bookForm");
        if (savedData) {
            const bookData = JSON.parse(savedData);
            setTitleState(bookData.title || "");
            setSubTitleState(bookData.subtitle || "");
            setAuthorState(bookData.author || "");
            setSummaryState(bookData.summary || "");
            setPaperBackAsinState(bookData.pb_asin || bookData.paper_back_asin || "");
            setKindleAsinState(bookData.k_asin || bookData.kindle_asin || "");
            setHardCoverAsinState(bookData.hc_asin || bookData.hard_cover_asin || "");
            setAudioAsinState(bookData.audio_asin || "");
            setAudibleAsinState(bookData.audible_asin || "");
            setAudioBookLinkState(bookData.audible_link || "");

            if (bookData.no_manuscript !== undefined) {
                setNoManuscript(storedBool(bookData.no_manuscript, false));
            }
            if (bookData.public_catalog_visible !== undefined) {
                setPublicCatalogVisible(storedBool(bookData.public_catalog_visible, true));
            }

            setTempPDFFile(bookData.book_temp_file_name || "");

            setTempPDFFile(bookData.temp_pdf_file || "");
            setPdfFileTitle(bookData.pdf_file_title || "");


            if (bookData.cover_file) {
                const restoredBlob = base64ToBlob(bookData.cover_file);
                const restoredFile = new File([restoredBlob], bookData.cover_file_name, { type: "image/jpeg" });

                setCoverFileState(restoredFile);
                setCoverPreview(bookData.cover_file);

            }


            const restoredLanguage = matchStoredLanguage(bookData.language) || bookData.language || {};
            const restoredGenre = matchStoredGenre(bookData.genre) || bookData.genre || {};
            book.setSelectedLanguage(restoredLanguage);
            book.setSelectedGenre(restoredGenre);

            setNewdBook((prev) => ({
                ...prev,
                ...getStoredBookTypes(bookData),
            }));
        }
    };


    useEffect(() => {
        const savedData = localStorage.getItem("bookForm");
        if (!savedData) return;

        try {
            const bookData = JSON.parse(savedData);
            if (!book.selectedLanguage?.id) {
                const restoredLanguage = matchStoredLanguage(bookData.language);
                if (restoredLanguage) book.setSelectedLanguage(restoredLanguage);
            }
            if (!book.selectedGenre?.id) {
                const restoredGenre = matchStoredGenre(bookData.genre);
                if (restoredGenre) book.setSelectedGenre(restoredGenre);
            }
        } catch (_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [book.languages?.length, book.genres?.length]);

    const clearBookDataFromLocalStorage = () => {
        localStorage.removeItem("bookForm");
    };


    const selectBookFile = (e) => {
        const input = e.target;
        const file = input.files[0];

        input.value = "";

        if (!file) {
            toast.error("Error: No file selected.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if (file.type !== "application/pdf") {
            toast.error(`Error: File "${file.name}" is not a PDF.`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        const maxFileSize = 500 * 1024 * 1024;
        if (file.size > maxFileSize) {
            toast.error(`Error: File "${file.name}" exceeds 500MB limit.`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        setPdfFile(file);
        setPdfFileTitle(file.name);

        saveBookDataToLocalStorage({
            pdf_file_title: file.name,
        });

        const formData = new FormData();
        formData.append("book_download_temp", file);

        try {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", process.env.REACT_APP_API_URL + "api/book/temp", true);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete >= 100 ? 99 : percentComplete);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {


                    const response = JSON.parse(xhr.responseText);
                    const tempFileName = response.tempPDFFile;

                    toast.success(response.message, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

                    setTempPDFFile(tempFileName);

                    saveBookDataToLocalStorage({
                        temp_pdf_file: tempFileName,
                    });

                    setUploadProgress(100);
                } else {
                    let errorMessage = `Upload failed: ${xhr.status}`;
                    try {
                        errorMessage = JSON.parse(xhr.responseText)?.message || errorMessage;
                    } catch (_) {}
                    toast.error(errorMessage, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                    setUploadProgress(-1);
                }
            };

            xhr.onerror = () => {
                toast.error("Error uploading file.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                setUploadProgress(-1);
            };

            xhr.send(formData);
        } catch (error) {
            toast.error(`Error sending file: ${error.message}`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    };

    const triggerFileInputBook = () => {
        document.getElementById("fileInputBook").click();
    };

    const triggerFileInput = () => {
        const input = document.getElementById("fileInput");
        input.value = "";
        input.click();
    };

    const handleFileChange = (e) => {
        const input = e.target;
        const file = e.target.files[0];
        input.value = "";
        if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageBase64 = event.target.result;

                setCoverPreview(imageBase64);
                setShowEditor(true);

                saveBookDataToLocalStorage({
                    cover_file: imageBase64,
                    cover_file_name: file.name,
                });


            };
            reader.readAsDataURL(file);
        }
    };

    const base64ToBlob = (base64) => {
        const byteCharacters = atob(base64.split(',')[1]);
        const byteArrays = [];
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArrays.push(byteCharacters.charCodeAt(i));
        }
        return new Blob([new Uint8Array(byteArrays)], { type: 'image/jpeg' });
    };


    const handleSave = () => {
        if (editorRef) {
            try {
                const canvas = editorRef.getImageScaledToCanvas();
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                                const editedImageBase64 = event.target.result;

                                setCoverPreview(editedImageBase64);
                                setCoverFileState(blob);
                                setShowEditor(false);

                                saveBookDataToLocalStorage({
                                    cover_file: editedImageBase64,
                                    cover_file_prev: editedImageBase64,
                                });


                            };
                            reader.readAsDataURL(blob);
                        } else {

                        }
                    },
                    'image/jpeg',
                    0.9
                );
            } catch (error) {

            }
        } else {

        }
    };

    const resetDataFields = () => {
        localStorage.removeItem("temp_pdf_file");

        setTempPDFFile(null);
        setPdfFileTitle('');

        setTitleState('');
        setSubTitleState('');
        setAuthorState('');
        setSummaryState('');
        setPaperBackAsinState('');
        setKindleAsinState('');
        setHardCoverAsinState('');
        setAudioAsinState('');
        setAudibleAsinState('');
        setAudioBookLinkState('');
        setPdfFile(null);
        setSelectedWordCount({});


        book.setSelectedLanguage({});
        book.setSelectedGenre({});

        setCoverFileState(null);
        setCoverPreview(defaultImage);
        setShowEditor(false);
        setScale(1);

        setEditorRef(null);
    };




    const addBook = async () => {
        setLoading(true);
        setError('');


        try {
            const formData = new FormData();

            formData.append('userId', user.user.id);
            formData.append('booksGenreId', book.selectedGenre.id);
            formData.append('booksPriceId', 1);
            formData.append('booksLanguageId', book.selectedLanguage.id);
            formData.append('booksTypeId', 1);
            formData.append('title', titleState);
            formData.append('subtitle', subtitleState);
            formData.append('author', authorState);
            formData.append('book_type', 'book');
            formData.append('language', book.selectedLanguage.language);
            formData.append('amz_price', '');
            formData.append('summary', summaryState);
            formData.append('status', 'NEW');
            formData.append('message', '');
            formData.append('message_flag', false);
            formData.append('amz_link', '');
            formData.append('paper_back_flag', newBook.paper_back_flag);
            formData.append('kindle_flag', newBook.kindle_flag);
            formData.append('kindle_unlimited_flag', newBook.kindle_unlimited_flag);
            formData.append('audio_exclusive_flag', newBook.audio_exclusive_flag);
            formData.append('audio_non_exclusive_flag', newBook.audio_non_exclusive_flag);
            formData.append('hard_cover_flag', newBook.hard_cover_flag);
            formData.append('audio_flag', newBook.audio_flag);
            formData.append('default_price', 500);
            formData.append('default_decrement', 0);
            formData.append('paper_back_asin', paperbackAsinState || '');
            formData.append('kindle_asin', kindleAsinState || '');
            formData.append('hard_cover_asin', hardcoverAsinState || '');
            formData.append('audio_asin', audioAsinState || '');
            formData.append('audio_audible_asin', audibleAsinState || '');
            formData.append('audio_download_link_ned', audioBookLinkState || '');
            formData.append('total_words', selectedWordCount?.count || 1000);
            formData.append('no_manuscript', noManuscript ? '1' : '0');
            formData.append('public_catalog_visible', publicCatalogVisible ? '1' : '0');

            if (!noManuscript && tempPDFFile) {
                formData.append("book_download", tempPDFFile);
            }

            if (coverFileState) {
                formData.append("cover_file", coverFileState);
            } else {
                toast.warn("No cover image found!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }

            formData.append("territories_mode", territoriesMode);

            if (territoriesMode === "WORLDWIDE") {
                formData.append("available_marketplaces", "[]");
            } else {
                formData.append("available_marketplaces", JSON.stringify(availableMarketplaces));
            }

            const result = await createBook(formData);

            const createdBook = result.book;

            toast.success(result.message, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

            const updatedBooks = await fetchCurrentUserBooks(user?.user?.id);
            book.setCurrentUserBooks(updatedBooks);
            book.setSelectedCurrentUserBook({formData});



            const foundBook = updatedBooks.find(b => b.id === createdBook.id);
            if (foundBook) {


                book.setSelectedCurrentUserBook(foundBook);
                user.setActiveComponent("BookFull");
            }

            clearBookDataFromLocalStorage();
            resetDataFields();

            } catch (e) {
            console.error(e);
            toast.error(`❌ Error: ${e.message}`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            setError(e.message || 'An error occurred while uploading your book.');
        } finally {
            setLoading(false);


        }
    };


    useEffect(() => {
        if (error) {
            toast.error(`❌ ${error}`, { closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'colored', position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    }, [error]);

    const addBookInDash = () => {
        user?.setActiveComponent("AddBookInDashboard");
    }
    const autoAddBookInDash = () => {
        user?.setActiveComponent("AddBookByGrabFromAmazon");
    }

    return (
        <>
        <ReviewerNameSafetyModal
            show={!!reviewerSafetyConflict}
            conflict={reviewerSafetyConflict}
            onCancel={() => setReviewerSafetyConflict(null)}
            onContinue={() => {
                setReviewerSafetyConflict(null);
                handleSubmitBookToModeration({ skipSafety: true });
            }}
        />
        <Card className="p-3 w-100 mt-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                {/* Header: title + Back button on step > 0 */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2 w-100">
                    <h5 className="text-muted mb-2 mb-md-0">
                        Manually Add a New Book to Virtalibry System:
                    </h5>

                    <ButtonGroup>
                        <Button
                            variant={user?.activeComponent === "AddBookByGrabFromAmazon" ? "success" : "outline-success"}
                            size="sm"
                            onClick={autoAddBookInDash}
                        >
                            Automatically
                        </Button>
                        <Button
                            variant={user?.activeComponent === "AddBookInDashboard" ? "success" : "outline-success"}
                            size="sm"
                            onClick={addBookInDash}
                        >
                            Manually
                        </Button>
                    </ButtonGroup>

                </div>
                <Col md={12} className="">

                        {nextStep.step0 ? (
                            <>
                                <div className="text-muted small">
                                    <p className="mb-1">
                                        This page allows you to quickly and easily add your book to the Virtalibry platform in a clear, step-by-step format. It only takes a couple of minutes.
                                    </p>
                                    <p className="mb-1">
                                        <strong>Step 1:</strong> Enter your <em>Book Title</em><br />
                                        <strong>Step 2:</strong> Add a <em>Subtitle</em> (optional)<br />
                                        <strong>Step 3:</strong> Provide the <em>Author Name or Pen Name</em> as it appears on Amazon.
                                    </p>
                                    <p className="mb-1">
                                        <strong>Note:</strong> Only the following characters are allowed in your inputs:
                                    </p>
                                    <p className="mb-1">
                                        <code>a–z, A–Z, 0–9, spaces, apostrophes (’), dashes (-), and these symbols: ! ? . , & % # * @</code>
                                    </p>
                                    <p className="mb-1">
                                        Cyrillic letters and unsupported special characters will be automatically removed.
                                    </p>
                                    <p className="mb-1">
                                        To make the process fast and pleasant — especially if you're copying from Amazon — we automatically clean up extra spaces and unnecessary symbols.
                                        For example, if you paste <code>"by Garrison Westwood (Author)"</code> in Author field, just as it appears on Amazon, we’ll recognize and extract the correct name: Garrison Westwood.
                                    </p>
                                    <p className="mb-0 fw-bolder">
                                        Let's get started!
                                    </p>
                                </div>
                                <Row className="mb-0 mt-3 px-0 small">
                                    <Col md={8}>

                                            <Form.Group className="mb-2">
                                                <Form.Label className="text-muted small mb-0"> <strong>Enter Book Title</strong></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    className="border border-secondary"
                                                    value={titleState}
                                                    onChange={e => {
                                                        const raw = e.target.value;

                                                        const cleaned = raw
                                                            .replace(/[а-яёА-ЯЁ]/g, '')
                                                            .replace(/:/g, '')          // удалить двоеточия
                                                            .replace(/[^a-zA-Z0-9\s'’\-!?.,&%#*@]/g, ''); // оставить нужные символы (без trim)

                                                        setTitleState(cleaned);
                                                    }}
                                                    placeholder="Enter Title of Your Book..."
                                                    disabled={loading}
                                                    required
                                                />

                                            </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="text-muted small mb-0"> <strong>Enter Author or Pen Name</strong></Form.Label>
                                            <Form.Control
                                                size="sm"
                                                className="border border-secondary"
                                                value={authorState}
                                                onChange={e => {
                                                    let raw = e.target.value;

                                                    let cleaned = raw
                                                        .replace(/^by\s+/i, '')
                                                        .replace(/\(Author\)$/i, '')
                                                        .replace(/[а-яёА-ЯЁ]/g, '')
                                                        .replace(/:/g, '')
                                                        .replace(/[^a-zA-Z0-9\s'’\-!?.,&%#*@]/g, '');   // оставляем только допустимые символы (без trim)

                                                    setAuthorState(cleaned);
                                                }}
                                                placeholder="Enter Author or Pen Name"
                                                disabled={loading}
                                                required
                                            />

                                        </Form.Group>

                                    </Col>

                                    <Col xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="text-muted small mb-0">
                                                <strong>Enter Book Subtitle</strong>
                                            </Form.Label>
                                            <Form.Control
                                                size="sm"
                                                className="border border-secondary"
                                                value={subtitleState}
                                                onChange={e => {
                                                    const raw = e.target.value;

                                                    const cleaned = raw
                                                        .replace(/[а-яёА-ЯЁ]/g, '')
                                                        .replace(/:/g, '')
                                                        .replace(/[^a-zA-Z0-9\s'’\-!?.,()&%#*@]/g, ''); // оставить допустимые символы (без trim)

                                                    setSubTitleState(cleaned);
                                                }}
                                                placeholder="Enter the Subtitle of Your Book, or type NO if there is none."
                                                disabled={loading}
                                                required
                                            />

                                        </Form.Group>

                                    </Col>

                                    <Col xs={12} className="d-flex justify-content-end mt-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-success d-flex align-items-center"
                                            onClick={() => handleStepsChange(step + 1)}
                                            disabled={loading}
                                        >
                                            Next <i className="ms-2 fas fa-arrow-circle-right"></i>
                                        </button>
                                    </Col>
                                </Row>

                            </>
                        ):('')}

                        {nextStep.step1 ? (
                            <>
                                <div className="text-muted small">


                                    <Row className="align-items-start mb-2">
                                        <Col xs={12} md={10}>
                                            <p className="mb-0">
                                                <strong>Step 4:</strong> Select the book's <em>language</em>, <em>genre</em>, <em>format</em>, and additional options.
                                                <br />
                                                <span className="text-muted">
                                  Choose the language and genre of your book, and mark all formats in which it is available on Amazon or Audible. These parameters determine what types of assignments will be available for your title.
                                       </span>
                                            </p>
                                        </Col>
                                        <Col xs={12} md={2} className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0">
                                            <button
                                                className="btn btn-outline-success btn-sm"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target="#collapseExample"
                                                aria-expanded={isOpen ? 'true' : 'false'}
                                                aria-controls="collapseExample"
                                                onClick={() => setIsOpen(!isOpen)}
                                            >
                                                Help?
                                            </button>
                                        </Col>
                                    </Row>

                                    <div >

                                        <div className={`collapse ${isOpen ? 'show' : ''}`} id="collapseExample">
                                            <div className="card card-body mb-3">
                                                <div className="mt-0">
                                                    <b className="fw-bold mb-2" >Book Type:</b>
                                                    <ul className="text-muted fst-italic">
                                                        <li>Kindle - If your book is available in Kindle format, select the corresponding checkbox.</li>
                                                        <li>Kindle Unlimited - If your Kindle version is available under the Kindle Unlimited program, select the corresponding checkbox.</li>
                                                        <li>Paperback - If your book is available in paperback format, select the corresponding checkbox.</li>
                                                        <li>Hardcover - If your book is available in hardcover format, select the corresponding checkbox.</li>
                                                        <li>Audiobook - If your book is available in audio format, select the corresponding checkbox.</li>
                                                    </ul>
                                                    <p className="fw-normal lh-sm text-danger fw-bolder mb-0 fst-italic">
                                                        Important: Amazon Audible offers two types of contracts for audiobooks—Exclusive and Non-Exclusive. Please specify which contract type applies to your book. Depending on the contract type, different assignment options for obtaining reviews will be available.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <Card className="rounded border-success-subtle mt-4 mb-3 p-3">

                                    <Row className=" mt-0" >
                                        <Col xs={12} md={3} className="d-flex flex-column justify-content-center align-items-stretch">
                                            <div className="d-flex flex-column mb-0 align-items-center">
                                                <div className="d-flex flex-column mb-0 align-items-start">
                                                <span className="mb-1 mt-0 text-muted"> <strong>Select Language of Book:</strong></span>
                                                <Dropdown className="mb-0">
                                                    <Dropdown.Toggle
                                                        variant="light"
                                                        size="sm"
                                                        style={{
                                                            width: '230px',
                                                            background: 'none',
                                                            border: '1px solid #ced4da',
                                                            color: '#6c757d',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        <span className="text-muted">{book.selectedLanguage.language || "Select Language"}</span>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu style={{ maxHeight: "230px", overflowY: "auto" }}>
                                                        {book.languages.map((lang) => (
                                                            <Dropdown.Item
                                                                onClick={() => book.setSelectedLanguage(lang)}
                                                                key={lang.id}
                                                                variant="success"
                                                                className="small text-muted"
                                                            >
                                                                {lang.language}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                            </div>

                                            <div className="d-flex flex-column mb-0 align-items-center">
                                                <div className="d-flex flex-column mb-0 align-items-start">
                                                <span className="mb-1 mt-2 text-muted"> <strong>Select Genre of Book:</strong></span>
                                                <Dropdown className="mb-0">
                                                    <Dropdown.Toggle
                                                        variant="light"
                                                        size="sm"
                                                        style={{
                                                            width: '230px',
                                                            background: 'none',
                                                            border: '1px solid #ced4da',
                                                            color: '#6c757d',
                                                            textAlign: 'left',
                                                            overflowY: "auto"
                                                        }}
                                                    >
                                                        <span className="text-muted">{book.selectedGenre.genre || "Select Genre"}</span>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu style={{ maxHeight: "230px", overflowY: "auto" }}>
                                                        {book.genres.map((gen) => (
                                                            <Dropdown.Item
                                                                onClick={() => book.setSelectedGenre(gen)}
                                                                key={gen.id}
                                                                variant="success"
                                                                className="small text-muted"
                                                            >
                                                                {gen.genre}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                                    </div>

                                            </div>
                                            <div className="d-flex flex-column mb-0 align-items-center">
                                                <div className="d-flex flex-column mb-0 align-items-start">
                                                    <span className="mb-1 mt-2 text-muted"> <strong>Select Word Count (Optional):</strong></span>
                                                    <Dropdown>
                                                        <Dropdown.Toggle
                                                            variant="light"
                                                            size="sm"
                                                            style={{
                                                                width: '230px',
                                                                background: 'none',
                                                                border: '1px solid #ced4da',
                                                                color: '#6c757d',
                                                                textAlign: 'left',
                                                                overflowY: "auto"
                                                            }}
                                                        >
                                                            {selectedWordCount?.label || "Choose Word Count..."}
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu style={{ maxHeight: "230px", overflowY: "auto" }}>
                                                            {wordCountOptions.map(option => (
                                                                <Dropdown.Item
                                                                    key={option.id}
                                                                    onClick={() => setSelectedWordCount(option)}
                                                                    className="small text-muted"
                                                                >
                                                                    {option.label}
                                                                </Dropdown.Item>
                                                            ))}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        </Col>


                                        <Col xs={12} md={5} className="d-flex flex-column justify-content-start align-items-stretch">
                                            <strong className="text-muted">Select the Available Types for this Book:</strong>
                                            <Card className=" rounded mt-1 mb-3 p-3 gap-0 ">
                                                <Form.Check
                                                   className="custom-checkbox mb-0"
                                                    label="This Book is Available in Kindle Version?"
                                                    name="kindle_flag"
                                                    checked={newBook.kindle_flag}
                                                    onChange={handleCheckboxChange}
                                                />

                                                <Form.Check
                                                    type="checkbox"
                                                    className="custom-checkbox mb-0"
                                                    label="This Book is Available in Paperback?"
                                                    name="paper_back_flag"
                                                    checked={newBook.paper_back_flag}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <Form.Check
                                                   className="custom-checkbox mb-0"
                                                    label="This Book is Available in Hardcover?"
                                                    name="hard_cover_flag"
                                                    checked={newBook.hard_cover_flag}
                                                    onChange={handleCheckboxChange}
                                                />

                                                <Form.Check
                                                    type="checkbox"
                                                    className="me-3 custom-checkbox mb-0"
                                                    label="This Book is Available in Audio Format?"
                                                    name="audio_flag"
                                                    checked={newBook.audio_flag}
                                                    onChange={handleCheckboxChange}
                                                />
                                            </Card>
                                        </Col>


                                        <Col xs={12} md={4}
                                             className="d-flex flex-column justify-content-start align-items-stretch">

                                            {newBook.kindle_flag ? (
                                                <>
                                                    <div>
                                                        <strong className="m-2 text-muted">Kindle Unlimited Option?</strong>
                                                        <Card className=" rounded mt-1 mb-3 p-3 gap-0 ">
                                                            <Form.Check
                                                                type="checkbox"
                                                                className="custom-checkbox mb-0"
                                                                label="Yes, it's Kindle Unlimited!"
                                                                name="kindle_unlimited_flag"
                                                                checked={newBook.kindle_unlimited_flag}
                                                                onChange={handleCheckboxChange}
                                                            />
                                                        </Card>
                                                    </div>
                                                </>
                                            ) : null}



                                            {newBook.audio_flag ? (
                                                <>
                                                    <div>
                                                        <strong className="m-2 text-muted">Type of Audio Distribution?</strong>
                                                        <Card className=" rounded mt-1 mb-3 p-3 gap-0 ">
                                                            <Form.Check
                                                                type="checkbox"
                                                                className="custom-checkbox mb-0"
                                                                label="Exclusive Distribution"
                                                                name="audio_exclusive_flag"
                                                                checked={newBook.audio_exclusive_flag}
                                                                onChange={handleCheckboxChange}
                                                            />
                                                            <Form.Check
                                                                type="checkbox"
                                                                className="custom-checkbox mb-0"
                                                                label="Non-Exclusive Distribution"
                                                                name="audio_non_exclusive_flag"
                                                                checked={newBook.audio_non_exclusive_flag}
                                                                onChange={handleCheckboxChange}
                                                            />
                                                        </Card>
                                                    </div>
                                                </>
                                            ) : null}



                                        </Col>
                                    </Row>
                                    </Card>

                                    <Card className="mt-0 mb-3 border">
                                        <Card.Header className="py-2">
<span className="text-muted">
  Choose <b>Worldwide</b> if your book or product is available on all Amazon marketplaces.
  Otherwise, select the specific marketplaces where it is available.
</span>


                                        </Card.Header>
                                        <Card.Body>
                                            <Col xs={12} md={12}>
                                                <label className="d-block mb-0 text-muted fw-semibold">Available Marketplaces:</label>

                                                {/* ROW 1: Select (left) + Selected/Show-Hide (right) */}
                                                <div className="d-flex align-items-start gap-2">
                                                    <Form.Select
                                                        size="sm"
                                                        className="border border-secondary text-muted"
                                                        value={territoriesMode}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setTerritoriesMode(v);

                                                            if (v === "WORLDWIDE") {
                                                                setAvailableMarketplaces([]);
                                                                setShowMarketplaces(false);
                                                            } else {
                                                                setShowMarketplaces(true);
                                                            }
                                                        }}
                                                        style={{ minWidth: 100, maxWidth: 200,  flex: "0 0 260px" }}
                                                    >
                                                        <option value="WORLDWIDE">Worldwide</option>
                                                        <option value="MARKETPLACES">Custom marketplaces</option>
                                                    </Form.Select>

                                                    {territoriesMode === "MARKETPLACES" && (
                                                        <div className="d-flex align-items-center justify-content-between flex-grow-1">
                                                            <div className="small">
                                                                {availableMarketplaces.length === 0 ? (
                                                                    <span className="text-danger fw-semibold">
              Pick at least one Amazon Marketplace
            </span>
                                                                ) : (
                                                                    <span className="text-muted">

        Available on:{" "}
                                                                        {availableMarketplaces.map((code, index) => {
                                                                            const marketplace = MARKETPLACE_OPTIONS.find((m) => m.code === code);
                                                                            return (
                                                                                <React.Fragment key={code}>
                                                                                    {index > 0 ? ", " : ""}
                                                                                    {marketplace ? renderMarketplaceLink(marketplace, `.${code}`) : `.${code}`}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                        {" "}(<b>{availableMarketplaces.length}</b> total)
            </span>
                                                                )}
                                                            </div>

                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                onClick={() => setShowMarketplaces((s) => !s)}
                                                            >
                                                                {showMarketplaces ? "Hide Amazon Marketplaces" : "Show Amazon Marketplaces"}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* ROW 2: checklist UNDER the dropdown */}
                                                {territoriesMode === "MARKETPLACES" && showMarketplaces && (
                                                    <div className="mt-2 border rounded p-2">
                                                        <div
                                                            className="d-grid gap-1"
                                                            style={{
                                                                gridTemplateColumns: "repeat(2, minmax(160px, 1fr))",
                                                                maxHeight: 160,
                                                                overflowY: "auto",
                                                            }}
                                                        >
                                                            {MARKETPLACE_OPTIONS.map((m) => (
                                                                <Form.Check
                                                                    key={m.code}
                                                                    type="checkbox"
                                                                    className="small text-muted mb-0"
                                                                    label={renderMarketplaceLink(m)}
                                                                    checked={availableMarketplaces.includes(m.code)}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        setAvailableMarketplaces((prev) =>
                                                                            checked
                                                                                ? Array.from(new Set([...prev, m.code]))
                                                                                : prev.filter((x) => x !== m.code)
                                                                        );
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>

                                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                onClick={() => setAvailableMarketplaces([])}
                                                            >
                                                                Clear
                                                            </Button>

                                                            <Button
                                                                size="sm"
                                                                variant="success"
                                                                onClick={() => {
                                                                    if (availableMarketplaces.length === 0) return;
                                                                    setShowMarketplaces(false);
                                                                }}
                                                                disabled={availableMarketplaces.length === 0}
                                                                title={availableMarketplaces.length === 0 ? "Pick at least one marketplace" : ""}
                                                            >
                                                                Done
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Col>
                                        </Card.Body>
                                    </Card>

                                    <div className="d-flex justify-content-end gap-2 mb-0">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => handleStepsChange(step - 1)}
                                            disabled={loading}
                                        >
                                            <i className="me-2 fas fa-arrow-circle-left"></i> Back
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={() => handleStepsChange(step + 1)}
                                            disabled={loading}
                                        >
                                            Next <i className="ms-2 fas fa-arrow-circle-right"></i>
                                        </button>
                                    </div>


                                </div>

                            </>
                        ) : null}


                            {nextStep.step2 && (
                                <>
                                    <div className="text-muted small">
                                        <Row className="mb-0 mt-0 ">
                                            <p className="mb-1">
                                                <strong>Step 5:</strong> Insert the ASINs for the formats you selected in Step 4.
                                            </p>

                                            <p className="text-muted">
                                                Please provide the 10-character ASINs corresponding to the book formats you selected earlier (e.g., Kindle, Paperback, Audio). Enter only the ASIN itself — without spaces or extra symbols.
                                                Each input field has a <strong>Check</strong> button that opens the corresponding Amazon or Audible page, so you can verify that the ASIN is correct.
                                            </p>
                                            <p className="text-muted">
                                                <strong>You can also paste a full Amazon or Audible link</strong> — and simply click the <em>Extract ASIN</em> button. The system will automatically extract the ASIN from the URL and fill the field for you.
                                            </p>
                                            <p className="text-muted mb-2">
                                                For example, try pasting:
                                                <br />
                                                <code>https://www.amazon.com/Let-Them-Theory-Life-Changing-Millions-ebook/dp/B0D978YKXT/ref=tmm_kin_swatch_0</code>
                                                    </p>
                                            <Row className="align-items-center mt-0 mb-4">
                                                <Col xs={12} md={6}>

                                                    <p className="text-muted mb-2">
                                                                                                               Then click <strong>Extract ASIN</strong> — and the field will automatically populate with <code>B0D978YKXT</code>.
                                                    </p>


                                                    <p className="text-muted">
                                                        Fast, clean, and hassle-free. Ready? Let’s go!
                                                    </p>
                                                </Col>

                                                <Col xs={12} md={6} className="text-end mt-0 mt-md-0">
                                                    <img
                                                        src="/img/site_img/gif_asin_extractor.gif"
                                                        alt="Scale and Format Instruction"
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '560px',
                                                            height: 'auto',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ccc',
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                                        }}
                                                    />
                                                </Col>
                                            </Row>




                                            {(newBook.paper_back_flag || newBook.kindle_flag) && (
                                                <Col md={6} style={{ maxWidth: "100%" }}>

                                                    {newBook.kindle_flag && (
                                                        <Form className="mt-0 mb-3">
                                                            <div className="small lh-3 fw-bold">Kindle ASIN:</div>
                                                            <div className="d-flex align-items-center gap-2 flex-nowrap">
                                                                <div className="flex-grow-1">
                                                                    <Form.Control
                                                                        className="border border-success form-control-sm"
                                                                        value={kindleAsinState}
                                                                        onChange={e => {
                                                                            const raw = e.target.value.trim();

                                                                            const cleaned = raw
                                                                                .replace(/[^ -~]/g, '')
                                                                                .toUpperCase();

                                                                            setKindleAsinState(cleaned);
                                                                        }}
                                                                        placeholder="Enter Kindle ASIN or paste Amazon link..."
                                                                        disabled={loading}
                                                                        required
                                                                    />
                                                                </div>

                                                                <Button
                                                                    variant="secondary"
                                                                    className="btn-sm"
                                                                    onClick={() => {
                                                                        const upperRaw = kindleAsinState.trim().toUpperCase();
                                                                        const asinMatch = upperRaw.match(/(?:\/dp\/|[?&]asin=)([A-Z0-9]{10})/i);

                                                                        if (asinMatch && asinMatch[1]) {
                                                                            setKindleAsinState(asinMatch[1]);
                                                                        } else {
                                                                            const cleaned = upperRaw.replace(/[^A-Z0-9]/g, '');
                                                                            if (/^[A-Z0-9]{10}$/.test(cleaned)) {
                                                                                setKindleAsinState(cleaned);
                                                                            } else {
                                                                                alert("Couldn't extract a valid ASIN. Please check the link.");
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    Extract ASIN
                                                                </Button>

                                                                <Button
                                                                    variant="info"
                                                                    className="btn-sm"
                                                                    disabled={!kindleAsinState || kindleAsinState.length !== 10}
                                                                    onClick={async () => {
                                                                        try {
                                                                            const targetUrl = `${user?.user?.amz_country}/dp/${kindleAsinState}`;

                                                                            const response = await getRedirectUrlApi(targetUrl);

                                                                            if (response && response.redirectUrl) {
                                                                                window.open(response.redirectUrl, '_blank', 'noopener noreferrer');
                                                                            } else {
                                                                                console.error('Failed to get redirect URL');
                                                                            }
                                                                        } catch (err) {
                                                                            console.error('Redirect error:', err);
                                                                        }
                                                                    }}

                                                                >
                                                                    Check
                                                                </Button>
                                                            </div>
                                                        </Form>


                                                    )}

                                                    {newBook.paper_back_flag && (
                                                        <Form className="mt-0 mb-3">
                                                            <div className="small lh-3 fw-bold">Paperback ASIN:</div>
                                                            <div className="d-flex align-items-center gap-2 flex-nowrap">
                                                                <div className="flex-grow-1">
                                                                    <Form.Control
                                                                        className="border border-success form-control-sm"
                                                                        value={paperbackAsinState}
                                                                        onChange={e => {
                                                                            const raw = e.target.value.trim();

                                                                            const cleaned = raw
                                                                                .replace(/[^ -~]/g, '')
                                                                                .toUpperCase();

                                                                            setPaperBackAsinState(cleaned);
                                                                        }}
                                                                        placeholder="Enter Paperback ASIN or paste Amazon link..."
                                                                        disabled={loading}
                                                                        required
                                                                    />

                                                                </div>

                                                                <Button
                                                                    variant="secondary"
                                                                    className="btn-sm"
                                                                    onClick={() => {
                                                                        const raw = paperbackAsinState.trim().toUpperCase();
                                                                        const asinMatch = raw.match(/(?:\/dp\/|[?&]asin=)([A-Z0-9]{10})/i);

                                                                        if (asinMatch && asinMatch[1]) {
                                                                            setPaperBackAsinState(asinMatch[1]);
                                                                        } else {
                                                                            const cleaned = raw.replace(/[^A-Z0-9]/g, '');
                                                                            if (/^[A-Z0-9]{10}$/.test(cleaned)) {
                                                                                setPaperBackAsinState(cleaned);
                                                                            } else {
                                                                                alert("Couldn't extract a valid ASIN. Please check the link.");
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    Extract ASIN
                                                                </Button>

                                                                <Button
                                                                    variant="info"
                                                                    className="btn-sm"
                                                                    disabled={!paperbackAsinState || paperbackAsinState.length !== 10}
                                                                    onClick={async () => {
                                                                        try {
                                                                            const targetUrl = `${user?.user?.amz_country}/dp/${paperbackAsinState}`;

                                                                            const response = await getRedirectUrlApi(targetUrl);

                                                                            if (response && response.redirectUrl) {
                                                                                window.open(response.redirectUrl, '_blank', 'noopener noreferrer');
                                                                            } else {
                                                                                console.error('Failed to get redirect URL');
                                                                            }
                                                                        } catch (err) {
                                                                            console.error('Redirect error:', err);
                                                                        }
                                                                    }}

                                                                >
                                                                    Check
                                                                </Button>
                                                            </div>
                                                        </Form>

                                                    )}

                                                    {(newBook.audio_non_exclusive_flag || newBook.audio_exclusive_flag || newBook.audio_flag) && (
                                                        <>
                                                            {(newBook.audio_non_exclusive_flag || newBook.audio_exclusive_flag) && (
                                                                <Form className="mt-0 mb-3">
                                                                    <div className="small lh-3 fw-bold">Audible ASIN:</div>
                                                                    <div className="d-flex align-items-center gap-2 flex-nowrap">
                                                                        <div className="flex-grow-1">
                                                                            <Form.Control
                                                                                className="border border-success form-control-sm"
                                                                                value={audibleAsinState}
                                                                                onChange={e => {
                                                                                    const raw = e.target.value.trim();

                                                                                    const cleaned = raw
                                                                                        .replace(/[^ -~]/g, '')
                                                                                       .toUpperCase();

                                                                                    setAudibleAsinState(cleaned);
                                                                                }}
                                                                                placeholder="Enter Audible ASIN or paste Audible link..."
                                                                                disabled={loading}
                                                                                required
                                                                            />

                                                                        </div>

                                                                        <Button
                                                                            variant="secondary"
                                                                            className="btn-sm"
                                                                            onClick={() => {
                                                                                const raw = audibleAsinState.trim().toUpperCase();

                                                                                const asinMatch = raw.match(/\/pd\/[^/]*\/([A-Z0-9]{10})/i)
                                                                                    || raw.match(/(?:\/dp\/|[?&]asin=)([A-Z0-9]{10})/i); // fallback

                                                                                if (asinMatch && asinMatch[1]) {
                                                                                    setAudibleAsinState(asinMatch[1]);
                                                                                } else {
                                                                                    const cleaned = raw.replace(/[^A-Z0-9]/g, '');
                                                                                    if (/^[A-Z0-9]{10}$/.test(cleaned)) {
                                                                                        setAudibleAsinState(cleaned);
                                                                                    } else {
                                                                                        alert("Couldn't extract a valid ASIN. Please check the link.");
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            Extract ASIN
                                                                        </Button>

                                                                        <Button
                                                                            variant="info"
                                                                            className="btn-sm"
                                                                            disabled={!audibleAsinState || audibleAsinState.length !== 10}
                                                                            onClick={async () => {
                                                                                try {
                                                                                    const targetUrl = `${user?.user?.audible_country}/pd/${audibleAsinState}`;

                                                                                    const response = await getRedirectUrlApi(targetUrl);

                                                                                    if (response && response.redirectUrl) {
                                                                                        window.open(response.redirectUrl, '_blank', 'noopener noreferrer');
                                                                                    } else {
                                                                                        console.error('Failed to get redirect URL');
                                                                                    }
                                                                                } catch (err) {
                                                                                    console.error('Redirect error:', err);
                                                                                }
                                                                            }}

                                                                        >
                                                                            Check
                                                                        </Button>
                                                                    </div>
                                                                </Form>


                                                            )}
                                                        </>
                                                    )}
                                                </Col>
                                            )}

                                            <Col md={newBook.paper_back_flag || newBook.kindle_flag ? 6 : 8} style={{ maxWidth: "50%" }}>
                                                {newBook.hard_cover_flag && (
                                                    <Form className="mt-0 mb-3">
                                                        <div className="small lh-3 fw-bold">Hardcover ASIN:</div>
                                                        <div className="d-flex align-items-center gap-2 flex-nowrap">
                                                            <div className="flex-grow-1">
                                                                <Form.Control
                                                                    className="border border-success form-control-sm"
                                                                    value={hardcoverAsinState}
                                                                    onChange={e => {
                                                                        const raw = e.target.value.trim();

                                                                        const cleaned = raw
                                                                            .replace(/[^ -~]/g, '')
                                                                            .toUpperCase();

                                                                        setHardCoverAsinState(cleaned);
                                                                    }}
                                                                    placeholder="Enter Hardcover ASIN or paste Amazon link..."
                                                                    disabled={loading}
                                                                    required
                                                                />

                                                            </div>

                                                            <Button
                                                                variant="secondary"
                                                                className="btn-sm"
                                                                onClick={() => {
                                                                    const raw = hardcoverAsinState.trim().toUpperCase();
                                                                    const asinMatch = raw.match(/(?:\/dp\/|[?&]asin=)([A-Z0-9]{10})/i);

                                                                    if (asinMatch && asinMatch[1]) {
                                                                        setHardCoverAsinState(asinMatch[1]);
                                                                    } else {
                                                                        const cleaned = raw.replace(/[^A-Z0-9]/g, '');
                                                                        if (/^[A-Z0-9]{10}$/.test(cleaned)) {
                                                                            setHardCoverAsinState(cleaned);
                                                                        } else {
                                                                            toast.info("Couldn't extract a valid ASIN. Please check the link.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                Extract ASIN
                                                            </Button>

                                                            <Button
                                                                variant="info"
                                                                className="btn-sm"
                                                                disabled={!hardcoverAsinState || hardcoverAsinState.length !== 10}
                                                                onClick={async () => {
                                                                    try {
                                                                        const targetUrl = `${user?.user?.amz_country}/dp/${hardcoverAsinState}`;

                                                                        const response = await getRedirectUrlApi(targetUrl);

                                                                        if (response && response.redirectUrl) {
                                                                            window.open(response.redirectUrl, '_blank', 'noopener noreferrer');
                                                                        } else {
                                                                            console.error('Failed to get redirect URL');
                                                                        }
                                                                    } catch (err) {
                                                                        console.error('Redirect error:', err);
                                                                    }
                                                                }}

                                                            >
                                                                Check
                                                            </Button>
                                                        </div>
                                                    </Form>

                                                )}

                                                {newBook.audio_flag && (
                                                    <Form className="mt-0 mb-3">
                                                        <div className="small lh-3 fw-bold">Amazon Audio ASIN:</div>
                                                        <div className="d-flex align-items-center gap-2 flex-nowrap">
                                                            <div className="flex-grow-1">
                                                                <Form.Control
                                                                    className="border border-success form-control-sm"
                                                                    value={audioAsinState}
                                                                    onChange={e => {
                                                                        const raw = e.target.value.trim();

                                                                        const cleaned = raw
                                                                            .replace(/[^ -~]/g, '')
                                                                            .toUpperCase();

                                                                        setAudioAsinState(cleaned);
                                                                    }}
                                                                    placeholder="Enter Amazon Audio ASIN or paste link..."
                                                                    disabled={loading}
                                                                    required
                                                                />

                                                            </div>

                                                            <Button
                                                                variant="secondary"
                                                                className="btn-sm"
                                                                onClick={() => {
                                                                    const raw = audioAsinState.trim().toUpperCase();
                                                                    const asinMatch = raw.match(/(?:\/dp\/|[?&]asin=)([A-Z0-9]{10})/i);

                                                                    if (asinMatch && asinMatch[1]) {
                                                                        setAudioAsinState(asinMatch[1]);
                                                                    } else {
                                                                        const cleaned = raw.replace(/[^A-Z0-9]/g, '');
                                                                        if (/^[A-Z0-9]{10}$/.test(cleaned)) {
                                                                            setAudioAsinState(cleaned);
                                                                        } else {
                                                                            alert("Couldn't extract a valid ASIN. Please check the link.");
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                Extract ASIN
                                                            </Button>

                                                            <Button
                                                                variant="info"
                                                                className="btn-sm"
                                                                disabled={!audioAsinState || audioAsinState.length !== 10}
                                                                onClick={async () => {
                                                                    try {
                                                                        const targetUrl = `${user?.user?.amz_country}/dp/${audioAsinState}`;

                                                                        const response = await getRedirectUrlApi(targetUrl);

                                                                        if (response && response.redirectUrl) {
                                                                            window.open(response.redirectUrl, '_blank', 'noopener noreferrer');
                                                                        } else {
                                                                            console.error('Failed to get redirect URL');
                                                                        }
                                                                    } catch (err) {
                                                                        console.error('Redirect error:', err);
                                                                    }
                                                                }}

                                                            >
                                                                Check
                                                            </Button>
                                                        </div>
                                                    </Form>

                                                )}



                                            </Col>


                                            <Row>
                                                {newBook.audio_non_exclusive_flag && (
                                                    <Form className="mt-0 mb-3">
                                                        <div className="small lh-3 fw-bold">Enter Link to Your Non-Exclusive
                                                            Audio Book:
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <Form.Control
                                                                className="border border-success form-control-sm"
                                                                value={audioBookLinkState}
                                                                onChange={e => setAudioBookLinkState(e.target.value)}
                                                                placeholder="Enter Link to Your Non-Exclusive Audio Book... https://drive.google.com/drive/"
                                                                disabled={loading}
                                                            />
                                                            <Button
                                                                variant="info"
                                                                className="ms-2 btn-sm"
                                                                disabled={!audioBookLinkState}
                                                                onClick={() => window.open(`${audioBookLinkState}`, '_blank',  'noopener noreferrer')}
                                                            >
                                                                Check
                                                            </Button>
                                                        </div>
                                                    </Form>
                                                )}
                                            </Row>
                                        </Row>

                                        <Row className="justify-content-end me-0 mb-0">

                                            <div className="mt-0 d-flex justify-content-end ms-0 gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary mb-0 btn-sm"
                                                    onClick={() => handleStepsChange(step - 1)}
                                                    disabled={loading}
                                                >
                                                    <i className="me-2 fas fa-arrow-circle-left"></i> Back
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-success mb-0 btn-sm"
                                                    onClick={() => handleStepsChange(step + 1)}
                                                    disabled={loading}
                                                >
                                                    Next <i className="ms-2 fas fa-arrow-circle-right"></i>
                                                </button>
                                            </div>
                                        </Row>
                                    </div>
                                </>
                            )}




                    <Row className="justify-content-center">
                                {nextStep.step3 && (
                    <>

                     <div className="text-muted small">
                                            <Row className="align-items-start mb-2">
                                                <Col xs={12} md={12}>
                                                    <p className="mb-0">
                                                        <strong>Step 6:</strong> Upload the <em>cover image</em> for your book and adjust it as needed.
                                                        <br />
                                                        <span className="text-muted">
                                                 Please upload the front cover of your book in high quality (JPG). After uploading, you’ll be able to zoom and crop it to match standard book dimensions (e.g., 6x9 or 8.5x11).
                                                      This image will be used in assignments and visual materials.
                                                        </span>
                                                    </p>

                                                </Col>

                                            </Row>
                                        </div>
                     <Row className="mt-0">
                            <Col md={4} className="p-3">
                                <div className="card text-center">
                                    <div className="d-flex justify-content-end align-items-center mt-2 mb-0">
                                        {coverPreview !== defaultImage && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm d-flex align-items-center me-2 justify-content-center"
                                                onClick={() => {
                                                    setCoverPreview(defaultImage);
                                                    setCoverFileState(null);
                                                    setShowEditor(false);
                                                    setScale(1);
                                                    saveBookDataToLocalStorage({ cover_file: null, cover_file_name: null });
                                                }}
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    padding: '0',
                                                }}
                                            >
                                                <i className="bi bi-x py-1" style={{ fontSize: '18px' }}></i>
                                            </button>
                                        )}
                                    </div>

                                    {showEditor ? (
                                        <>
                                            <div ref={parentRef} style={{ width: '100%', maxWidth: '260px', margin: '0 auto' }}>
                                                <AvatarEditor
                                                    ref={(ref) => setEditorRef(ref)}
                                                    image={coverPreview}
                                                    width={editorSize.width}
                                                    height={editorSize.height}
                                                    border={0}
                                                    scale={scale}
                                                    borderRadius={0}
                                                    className="rounded shadow"
                                                />
                                            </div>

                                            <div className="d-flex justify-content-center align-items-center my-2">
                                                <i className="bi bi-dash-circle-fill text-success me-2" style={{ fontSize: '1.2rem' }}></i>
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="2"
                                                    step="0.01"
                                                    value={scale}
                                                    onChange={(e) => setScale(parseFloat(e.target.value))}
                                                    style={{ width: '150px' }}
                                                />
                                                <i className="bi bi-plus-circle-fill text-success ms-2" style={{ fontSize: '1.2rem' }}></i>
                                            </div>

                                            <div className="d-flex justify-content-center gap-2 mb-2">
                                                <select
                                                    value={bookFormat}
                                                    onChange={(e) => setBookFormat(e.target.value)}
                                                    className="form-select form-select-sm w-auto"
                                                >
                                                    <option value="6x9">6x9 (2x3)</option>
                                                    <option value="8.5x11">8.5x11</option>
                                                    <option value="8.5x8.5">8.5x8.5</option>
                                                    <option value="8x10">8x10</option>
                                                    <option value="5x8">5x8</option>
                                                    <option value="7x10">7x10</option>
                                                </select>

                                                <button className="btn btn-success btn-sm" onClick={handleSave}>Save</button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="p-2" style={{ position: 'relative' }}>
                                            <img
                                                src={coverPreview}
                                                alt="Cover Preview"
                                                className="img-fluid rounded mt-2 mb-3"
                                                style={{ maxHeight: '400px', opacity: coverPreview !== defaultImage ? 1 : 0.5 }}
                                            />
                                            {!coverFileState && (<>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-success"
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    padding: '6px 12px',
                                                    borderRadius: '5px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                }}
                                                onClick={triggerFileInput}
                                                disabled={loading}
                                            >
                                                <i className="bi bi-upload"></i> Upload Cover
                                            </button>
                                            </>)}
                                        </div>
                                    )}

                                    <input type="file" id="fileInput" style={{ display: 'none' }} onChange={handleFileChange} />
                                </div>
                            </Col>

                            <Col md={8}>
                                <div className="text-muted small p-2 mt-1 ps-3">
                                    <h6>Instructions for Scale and Size Format:</h6>
                                    <p className="mb-1">
                                        After uploading your book cover, use the slider to adjust zoom and choose the appropriate book size format to match Amazon dimensions.
                                    </p>
                                    <div className="mt-3 mb-3 text-center">
                                        <img
                                            src="/img/site_img/gif_scale_format.gif"
                                            alt="Scale and Format Instruction"
                                            style={{
                                                maxWidth: '60%',
                                                height: 'auto',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </div>
                                    <p className="mb-1">
                                        We recommend choosing the correct ratio (e.g., 6x9 or 8.5x11) that matches your real book’s layout. This helps us show a precise representation of your title in promotional materials.
                                    </p>

                                    <p className="mb-1">
                                        Use the <strong>plus/minus</strong> icons to fine-tune zoom, then press <strong>Save</strong> to finalize the cover image.
                                    </p>

                                </div>
                            </Col>

                        </Row>
                     <Row w={100} className="mb-0 m-auto justify-content-center ">

                                                <div className="d-flex justify-content-end gap-2 mb-0">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary fflex-md-row btn-sm "
                                                        onClick={() => handleStepsChange(step-1)}
                                                        disabled={loading}
                                                    >
                                                        <i className="me-2 fas fa-arrow-circle-left"></i> Back
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success fflex-md-row btn-sm "
                                                        onClick={() => handleStepsChange(step+1)}
                                                        disabled={loading}
                                                    >
                                                        Next <i className="ms-2 fas fa-arrow-circle-right"></i>
                                                    </button>
                                                </div>
                                            </Row>
                    </>
                                )}
                    </Row>


                    <Row className="justify-content-center">
                            <Col md={12} className="mt-0">

                        {nextStep.step4 && (
                                    <>
                                        <div className="text-muted small">
                                            <p className="text-muted">
                                                <strong>Step 7:</strong> Provide a short and engaging summary of your book. This description helps readers understand what your book is about and why it might interest them. Try to keep it concise, clear, and spoiler-free.
                                                <br />
                                                Focus on the central theme, main conflict, or emotional journey — what makes your book stand out. Avoid generic marketing phrases and instead aim to spark genuine curiosity.
                                            </p>



                                            <Form className="mt-0 mb-2 p-2 border-success">
                                                <Form.Control
                                                    as="textarea"
                                                    rows={5}
                                                    value={summaryState}
                                                    onChange={(e) => {
                                                        let raw = e.target.value;

                                                        const cleaned = raw
                                                            .replace(/[а-яёА-ЯЁ]/g, '')
                                                            .replace(/[^\x20-\x7E\r\n\-*•0-9a-zA-Z'"“”’!?.,&#*@]/g, '')
                                                            .replace(/(.)\1{4,}/g, '$1$1')
                                                            .replace(/[ \t]{2,}/g, ' ')
                                                            .split('\n')
                                                            .map(line => line.trim())
                                                            .join('\n')
                                                            .replace(/\n{3,}/g, '\n\n');


                                                        setSummaryState(cleaned);
                                                    }}
                                                    placeholder="Enter a concise and meaningful book summary here..."
                                                    disabled={loading}
                                                    className="text-muted"
                                                    style={{ fontSize: 14 }}
                                                />
                                            </Form>

                                            <>
                                                    <Row className="justify-content-end me-1 mb-0">

                                                        <div className="mt-3 d-flex justify-content-end ms-4 gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm mb-0"
                                                                onClick={() => handleStepsChange(step - 1)}
                                                                disabled={loading}
                                                            >
                                                                <i className="me-2 fas fa-arrow-circle-left"></i> Back
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-success btn-sm mb-0"
                                                                onClick={() => handleStepsChange(step + 1)}
                                                                disabled={loading}
                                                            >
                                                                Next <i className="ms-2 fas fa-arrow-circle-right"></i>
                                                            </button>
                                                        </div>
                                                    </Row>

                                                </>

                                        </div>
                                    </>
                                )}
                        {nextStep.step5 && (
                                    <>
                                        <div className="text-muted small">
                                            <p className="">
                                                <strong className="">Step 8:</strong> Upload Book File (PDF up to 500 Mb):
                                            </p>
                                            <p className="text-muted ">
                                                Please upload the full content of your book in PDF format. This file is used only for internal processing and review purposes — it will never be shared publicly or displayed to readers. Make sure your file includes the complete text, formatted properly and ready for evaluation.
                                            </p>
                                            <p className="text-muted mb-1">
                                                The maximum file size is <strong>500 Mb</strong>. If your book file is larger — no worries! You can easily reduce the size without compromising quality using the free tool{" "}
                                                <a href="https://www.ilovepdf.com/compress_pdf" target="_blank" rel="noopener noreferrer">
                                                    iLovePDF
                                                </a>.
                                                Simply upload your PDF there, choose “Recommended compression,” and download the optimized version.
                                            </p>
                                            <p className="text-muted  mb-2">
                                                In most cases, compression reduces the file size by <strong>3×–10×</strong> while keeping your layout, images, and typography intact. This helps speed up processing and ensures smooth handling of your book on our platform.
                                            </p>
                                            <p className="text-muted  fst-italic">
                                                Tip: Avoid scanned or image-only PDFs — make sure your book text is selectable and embedded as real text for best compatibility.
                                            </p>


                                            <Card className="p-0 border-success mt-0 mb-2">
                                                <Card.Header className="py-1">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="public-catalog-visible"
                                                        label={
                                                            <span className="ms-1">
                                                                <span className="fw-semibold">I want to place my book in the public book library</span>
                                                                {publicCatalogVisible
                                                                    ? " — Discoverable in the public ARC library."
                                                                    : " — Your book won't be publicly listed."}
                                                            </span>
                                                        }
                                                        checked={publicCatalogVisible}
                                                        onChange={(e) => {
                                                            const v = e.target.checked;
                                                            setPublicCatalogVisible(v);
                                                            saveBookDataToLocalStorage({ public_catalog_visible: v });
                                                        }}
                                                        className="custom-checkbox mt-2 me-2 mb-1"
                                                    />
                                                </Card.Header>
                                            </Card>

                                            <Card className="mt-2 border border-secondary bg-secondary bg-opacity-10">
                                                <Card.Header className="py-1">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="no-manuscript"
                                                        label={
                                                            <span className="ms-1">
      <span className="fw-semibold">I don’t want to upload the manuscript</span>
                                                                {" — Universal assignments will be unavailable."}
    </span>
                                                        }
                                                        checked={noManuscript}
                                                        onChange={(e) => {
                                                            const v = e.target.checked;
                                                            setNoManuscript(v);
                                                            saveBookDataToLocalStorage({ no_manuscript: v });
                                                            if (v) {
                                                                setPdfFile(null);
                                                                setPdfFileTitle('');
                                                                setTempPDFFile('');
                                                                setUploadProgress(0);
                                                                localStorage.removeItem("temp_pdf_file");
                                                                localStorage.removeItem("pdf_file_title");
                                                                saveBookDataToLocalStorage({ temp_pdf_file: '', pdf_file_title: '' });
                                                            }
                                                        }}
                                                        className="custom-checkbox mt-2 mb-1"
                                                    />
                                                </Card.Header>
                                                {!noManuscript && (
                                                    <Card.Body>
                                                        <Row className="mb-2">
                                                            <span className="mx-auto text-muted mb-3 text-start fw-semibold">
                                                                Upload Your Book Manuscript File (PDF up to 500 Mb)
                                                            </span>

                                                            <div className="d-flex align-items-center mt-0 justify-content-between w-100">
                                                                <Button variant="danger" disabled={noManuscript} onClick={triggerFileInputBook} className="me-3 btn btn-sm">
                                                                    Upload File
                                                                </Button>

                                                                {uploadProgress > 0 && (
                                                                    <div
                                                                        className="progress flex-grow-1"
                                                                        role="progressbar"
                                                                        aria-valuenow={uploadProgress}
                                                                        aria-valuemin="0"
                                                                        aria-valuemax="90"
                                                                        style={{ height: "30px" }}
                                                                    >
                                                                        <div
                                                                            className={`progress-bar ${uploadProgress === 100 ? "bg-success" : "bg-info"}`}
                                                                            style={{ width: `${uploadProgress}%` }}
                                                                        >
                                                                            {uploadProgress === 100
                                                                                ? (
                                                                                    <span className="fw-semibold fst-normal">
                                                                                        Uploading {pdfFileTitle.length > 90
                                                                                            ? pdfFileTitle.slice(0, 90) + '...'
                                                                                            : pdfFileTitle
                                                                                        } is Complete!
                                                                                    </span>
                                                                                )
                                                                                : uploadProgress === 99
                                                                                    ? <span className="fw-semibold fst-normal">Processing manuscript: watermarking and compressing...</span>
                                                                                    : `${uploadProgress}%`
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {(pdfFileTitle && !uploadProgress) && (
                                                                    <Col className="text-center shadow-sm rounded-1 border border-secondary py-1 small">
                                                                        <strong className="text-muted d-block">Uploaded: {pdfFileTitle}</strong>
                                                                    </Col>
                                                                )}
                                                                {(uploadProgress !== 100 && !pdfFileTitle) && (
                                                                    <Col className="text-center shadow-sm rounded-1 border border-secondary py-1 small">
                                                                        <strong className="text-muted d-block">Press "Upload File" to begin uploading.</strong>
                                                                    </Col>
                                                                )}
                                                            </div>

                                                            <Form.Group className="mb-0">
                                                                <Form.Control
                                                                    type="file"
                                                                    accept="application/pdf"
                                                                    id="fileInputBook"
                                                                    onChange={selectBookFile}
                                                                    style={{ display: "none" }}
                                                                />
                                                            </Form.Group>

                                                            <div className="d-flex justify-content-end">
                                                                <p className="mb-0 text-muted small fst-italic text-end">
                                                                    {uploadProgress > 0 && uploadProgress < 100
                                                                        ? "Processing manuscript: watermarking and compressing. Please wait..."
                                                                        : uploadProgress !== 100 && !pdfFileTitle
                                                                            ? "After uploading your book file, you'll be able to submit it for moderation."
                                                                            : "Click Submit for Moderation to send it to moderation."}
                                                                </p>
                                                            </div>
                                                        </Row>
                                                    </Card.Body>
                                                )}
                                            </Card>



                                            <div className="mt-4 mb-3 d-flex justify-content-end ms-0 gap-0">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => handleStepsChange(step - 1)}
                                                    disabled={loading}
                                                >
                                                    <i className="me-2 fas fa-arrow-circle-left"></i> Back
                                                </button>
                                            </div>

                                        </div>
                                        <></>
                                        <div className="mt-auto d-flex justify-content-end">
                                            <Button variant="success"
                                                    type="button"
                                                    className="btn btn-sm"
                                                    onClick={handleSubmitBookToModeration}
                                                    disabled={loading || manuscriptUploadInProgress || !manuscriptRequirementSatisfied}>
                                                {loading ? (
                                                    <>
                                                        Sending Book Data&nbsp;
                                                        <Spinner animation="border" size="sm" />
                                                    </>
                                                ) : uploadProgress > 0 && uploadProgress < 100 ? (
                                                    <>
                                                        {uploadProgress === 99 ? "Processing Manuscript" : "Uploading Your File"}&nbsp;
                                                        <Spinner animation="border" size="sm" />
                                                    </>
                                                ) : !manuscriptRequirementSatisfied ? (
                                                    'Upload Manuscript Please'
                                                ) : (
                                                    'Submit for Moderation'
                                                )}


                                            </Button>
                                            </div>
                                    </>
                                )}



                            </Col>
                        </Row>


                </Col>



                </div>


        </Card>
        </>
    );
});

export default addBookInDashboard;
